/**
 * F14.1 — Strings de i18n. Objeto namespaced por área (auth, common,
 * etc.). Cada idioma deve ter exatamente as mesmas chaves — o tipo
 * `Messages` garante via inferência.
 *
 * Adicionar nova string:
 *   1. Inclua a key em `pt` (master);
 *   2. Replique em `fr` e `en` (TypeScript reclama se faltar);
 *   3. Use `useT()` em qualquer componente: `t("auth.email")`.
 *
 * Nesta iteração F14.3 só temos strings das 4 telas auth + commons +
 * o LanguageSwitcher. F14.5 estende com Sidebar/Header/Dashboard.
 */

import { ptPages, frPages, enPages, type PagesMessages } from "./messages.pages.js";
import {
  esAuth,
  esCommon,
  esLangSwitcher,
  esLanding,
  esTechPortal,
  esTechSignup,
} from "./messages.es.js";
import {
  ukAuth,
  ukCommon,
  ukLangSwitcher,
  ukLanding,
  ukTechPortal,
  ukTechSignup,
} from "./messages.uk.js";

export const LOCALES = ["fr", "pt", "en", "es", "uk"] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_LABELS: Record<Locale, { flag: string; short: string; label: string }> = {
  fr: { flag: "🇫🇷", short: "FR", label: "Français" },
  pt: { flag: "🇧🇷", short: "PT", label: "Português" },
  en: { flag: "🇬🇧", short: "EN", label: "English" },
  es: { flag: "🇪🇸", short: "ES", label: "Español" },
  uk: { flag: "🇺🇦", short: "UA", label: "Українська" },
};

export const DEFAULT_LOCALE: Locale = "fr";

/* ─── Shape (interface) — keys + tipo string. Valores literais não
       vão para o tipo, então cada idioma pode ter strings diferentes. */

export interface Messages {
  common: {
    loading: string;
    cancel: string;
    submit: string;
    back: string;
    next: string;
    previous: string;
    save: string;
    close: string;
    logout: string;
    logoutPending: string;
    showPassword: string;
    hidePassword: string;
    /** F1 redesenho — etiqueta ao lado do rótulo em campo obrigatório. */
    requiredField: string;
  };
  langSwitcher: {
    ariaLabel: string;
  };
  auth: {
    subtitle: string;
    emailLabel: string;
    emailPlaceholder: string;
    passwordLabel: string;
    passwordPlaceholder: string;
    newPasswordLabel: string;
    confirmPasswordLabel: string;
    nameLabel: string;
    submitLogin: string;
    submitLoginPending: string;
    submitForgot: string;
    submitForgotPending: string;
    submitReset: string;
    submitResetPending: string;
    submitAccept: string;
    submitAcceptPending: string;
    forgotPasswordLink: string;
    backToLoginLink: string;
    forgotTitle: string;
    forgotDescription: string;
    forgotSent: string;
    resetTitle: string;
    resetDescription: string;
    resetSuccess: string;
    acceptTitle: string;
    acceptDescription: string;
    acceptSuccess: string;
    errorPasswordsDoNotMatch: string;
    errorTokenInvalid: string;
  };
  /** F14.5 — Sidebar/Header global + Dashboard. */
  nav: {
    dashboardTitle: string;
    dashboardSubtitle: string;
    sectionMasterData: string;
    sectionOperations: string;
    sectionBilling: string;
    sectionFiscalReports: string;
    sectionBank: string;
    sectionPurchases: string;
    sectionFiscal: string;
    sectionFinance: string;
    sectionAdmin: string;
    /** CEO 2026-11-01 — grupo Clientes e equipe. */
    sectionPeople: string;
    customers: string;
    vehicles: string;
    technicians: string;
    bankAccounts: string;
    dossiers: string;
    importEdsp: string;
    proformas: string;
    faturas: string;
    controle: string;
    bankExtracts: string;
    partnerAccount: string;
    suppliers: string;
    purchaseInvoices: string;
    fiscalDashboard: string;
    fiscalResultat: string;
    fiscalSig: string;
    fiscalSeuil: string;
    fiscalBilan: string;
    fiscalBudget: string;
    fiscalEntries: string;
    fiscalTva: string;
    stormFin: string;
    financeImportar: string;
    financeLancamentos: string;
    financeKpis: string;
    financeFechamento: string;
    companiesAdmin: string;
    users: string;
    teamNotices: string;
    tasks: string;
    invites: string;
    auditLog: string;
    profileTitle: string;
    profileName: string;
    profileEmail: string;
    profileSuperAdmin: string;
    profileMemberships: string;
    profileMembershipsNone: string;
    companiesAccessible: string;
    companiesNone: string;
    inactive: string;
  };
  /** F-design — ErpShell (busca, leitura, papéis) + nav extra. */
  shell: {
    searchPlaceholder: string;
    bankPendingBadgeTitle: string;
    openMenu: string;
    idleTitle: string;
    idleBody: string;
    idleStay: string;
    idleLogoutNow: string;
    searchLoading: string;
    searchEmpty: string;
    readOnly: string;
    notifications: string;
    themeLight: string;
    themeDark: string;
    sectionReports: string;
    reportConsolidated: string;
    technicianPayments: string;
    companyPrimary: string;
    companyGroup: string;
    roleSuperAdmin: string;
    roleAdmin: string;
    roleUser: string;
    roleViewer: string;
  };
  /** F-design — Dashboard KPIs + período. */
  dash: {
    groupPerformance: string;
    groupTreasury: string;
    treasuryCurrent: string;
    periodMonth: string;
    periodQuarter: string;
    periodYear: string;
    periodAll: string;
    periodAllLabel: string;
    kpiCaFacture: string;
    kpiBenefice: string;
    kpiDevise: string;
    kpiDossiers: string;
    kpiArClients: string;
    kpiOverdue: string;
    kpiApSuppliers: string;
    kpiApTechnicians: string;
    unitDossiers: string;
    unitInvoices: string;
    ofRevenue: string;
    marge: string;
    totalQuotes: string;
    currentPeriod: string;
    overdueDetail: string;
    intraGroupReceivable: string;
  };
  landing: {
    navCta: string;
    techSignupLink: string;
    heroEyebrow: string;
    heroTitlePre: string;
    heroTitleAccent: string;
    heroSubtitle: string;
    featuresTitle: string;
    featuresSubtitle: string;
    f1t: string;
    f1d: string;
    f2t: string;
    f2d: string;
    f3t: string;
    f3d: string;
    f4t: string;
    f4d: string;
    f5t: string;
    f5d: string;
    f6t: string;
    f6d: string;
    f7t: string;
    f7d: string;
    f8t: string;
    f8d: string;
    b1t: string;
    b1d: string;
    b2t: string;
    b2d: string;
    b3t: string;
    b3d: string;
    finalTitlePre: string;
    finalTitleAccent: string;
    finalSubtitle: string;
    footerRights: string;
  };
}

/* ─── Source of truth: PT (idioma do CEO + base de tradução) ──── */

const pt: Messages = {
  common: {
    loading: "Carregando…",
    cancel: "Cancelar",
    submit: "Enviar",
    back: "Voltar",
    next: "Próximo",
    previous: "Anterior",
    save: "Salvar",
    close: "Fechar",
    logout: "Sair",
    logoutPending: "Saindo…",
    showPassword: "Mostrar senha",
    hidePassword: "Ocultar senha",
    requiredField: "Campo obrigatório",
  },
  langSwitcher: {
    ariaLabel: "Idioma",
  },
  auth: {
    subtitle: "Sistema de gestão",
    emailLabel: "E-mail",
    emailPlaceholder: "seu@email.com",
    passwordLabel: "Senha",
    passwordPlaceholder: "••••••••",
    newPasswordLabel: "Nova senha",
    confirmPasswordLabel: "Confirmar senha",
    nameLabel: "Nome completo",
    submitLogin: "Entrar",
    submitLoginPending: "Entrando…",
    submitForgot: "Enviar link",
    submitForgotPending: "Enviando…",
    submitReset: "Redefinir senha",
    submitResetPending: "Redefinindo…",
    submitAccept: "Aceitar convite",
    submitAcceptPending: "Aceitando…",
    forgotPasswordLink: "Esqueci minha senha",
    backToLoginLink: "Voltar ao login",
    forgotTitle: "Recuperar senha",
    forgotDescription: "Digite seu e-mail e enviaremos um link para redefinir a senha.",
    forgotSent: "Enviado! Verifique seu e-mail.",
    resetTitle: "Definir nova senha",
    resetDescription: "Escolha uma senha forte (mínimo 8 caracteres).",
    resetSuccess: "Senha redefinida com sucesso. Redirecionando…",
    acceptTitle: "Aceitar convite",
    acceptDescription: "Crie sua senha para finalizar o cadastro.",
    acceptSuccess: "Cadastro concluído! Redirecionando…",
    errorPasswordsDoNotMatch: "As senhas não coincidem.",
    errorTokenInvalid: "Link inválido ou expirado.",
  },
  nav: {
    dashboardTitle: "Painel",
    dashboardSubtitle: "Painel de controle",
    sectionMasterData: "Cadastros",
    sectionOperations: "Operação",
    sectionBilling: "Faturação e repasses",
    sectionFiscalReports: "Fiscal e relatórios",
    sectionBank: "Banco",
    sectionPurchases: "Compras",
    sectionFiscal: "Fiscal",
    sectionFinance: "Banco e finanças",
    sectionAdmin: "Ferramentas e administração",
    sectionPeople: "Clientes e equipe",
    customers: "Clientes",
    vehicles: "Cadastro de veículos",
    technicians: "Técnicos",
    bankAccounts: "Contas bancárias",
    dossiers: "Carros",
    importEdsp: "Importação eDSP",
    proformas: "Proformas",
    faturas: "Faturas",
    controle: "A receber e a pagar",
    bankExtracts: "Extratos & Reconciliação",
    partnerAccount: "Conta do sócio",
    suppliers: "Fornecedores",
    purchaseInvoices: "Faturas de compra",
    fiscalDashboard: "KPIs Fiscais",
    fiscalResultat: "DRE",
    fiscalSig: "SIG",
    fiscalSeuil: "Ponto de equilíbrio",
    fiscalBilan: "Balanço",
    fiscalBudget: "Orçamento N+1",
    fiscalEntries: "Lançamentos",
    fiscalTva: "IVA",
    stormFin: "Storm.Fin (contabilidade) ↗",
    financeImportar: "Importar",
    financeLancamentos: "Lançamentos",
    financeKpis: "Tesouraria",
    financeFechamento: "Fechamento",
    companiesAdmin: "Empresas",
    users: "Usuários",
    teamNotices: "Recados",
    tasks: "Tarefas",
    invites: "Convites",
    auditLog: "Log de auditoria",
    profileTitle: "Seu perfil",
    profileName: "Nome",
    profileEmail: "E-mail",
    profileSuperAdmin: "Super admin",
    profileMemberships: "Memberships",
    profileMembershipsNone: "nenhuma",
    companiesAccessible: "Empresas acessíveis",
    companiesNone: "Nenhuma empresa disponível.",
    inactive: "inativa",
  },
  shell: {
    searchPlaceholder: "Buscar (nº fatura, dossiê, placa, cliente)…",
    bankPendingBadgeTitle: "Transações bancárias pendentes de tratamento",
    openMenu: "Abrir menu",
    idleTitle: "Sessão prestes a expirar",
    idleBody: "Detectamos inatividade. Por segurança, você será desconectado automaticamente em:",
    idleStay: "Continuar conectado",
    idleLogoutNow: "Sair agora",
    searchLoading: "Buscando…",
    searchEmpty: "Nenhum dossiê encontrado.",
    readOnly: "Somente leitura",
    notifications: "Notificações",
    themeLight: "Tema claro",
    themeDark: "Tema escuro",
    sectionReports: "Relatórios",
    reportConsolidated: "Relatório consolidado",
    technicianPayments: "Pagamentos de técnicos",
    companyPrimary: "empresa principal",
    companyGroup: "grupo",
    roleSuperAdmin: "Super admin",
    roleAdmin: "Admin",
    roleUser: "Usuário",
    roleViewer: "Visualizador",
  },
  dash: {
    groupPerformance: "Desempenho",
    groupTreasury: "Tesouraria",
    treasuryCurrent: "saldo atual",
    periodMonth: "Mês",
    periodQuarter: "Trim.",
    periodYear: "Ano",
    periodAll: "Tudo",
    periodAllLabel: "todos os períodos",
    kpiCaFacture: "Faturado (líq.)",
    kpiBenefice: "Lucro previsto (ES)",
    kpiDevise: "Total orçado",
    kpiDossiers: "Dossiês",
    kpiArClients: "A receber · clientes",
    kpiOverdue: "Em atraso · clientes",
    kpiApSuppliers: "A pagar · fornecedores",
    kpiApTechnicians: "A pagar · técnicos",
    unitDossiers: "dossiês",
    unitInvoices: "faturas",
    ofRevenue: "do faturado",
    marge: "margem",
    totalQuotes: "total dos orçamentos",
    currentPeriod: "período atual",
    overdueDetail: "vencimento ultrapassado",
    intraGroupReceivable: "Intragrupo a receber",
  },
  landing: {
    navCta: "Acessar sistema",
    techSignupLink: "Sou técnico — criar minha conta",
    heroEyebrow: "ERP financeiro · débosselage",
    heroTitlePre: "Do dossiê ao contador,",
    heroTitleAccent: "num só lugar.",
    heroSubtitle:
      "Comissões, faturamento, conciliação bancária e fiscal francês — multiempresa, em tempo real. O sistema de gestão feito para a operação de reparo de granizo do grupo Storm.",
    featuresTitle: "Tudo que a operação precisa",
    featuresSubtitle:
      "Da entrada do veículo ao fechamento contábil — um fluxo único, sem planilhas paralelas.",
    f1t: "Dossiês & comissões",
    f1d: "Cada reparo num só lugar: linhas de serviço, cascata de comissão (cliente · técnico · intermediário) e lucro previsto, em tempo real.",
    f2t: "Faturamento completo",
    f2d: "Faturas a cliente, técnico e intra-grupo (proformas) — PDF automático, envio por e-mail e notas de crédito.",
    f3t: "Conciliação bancária",
    f3d: "Importe o extrato e concilie com poucos cliques: recebimentos, pagamentos, impostos e taxas — com comprovantes anexados.",
    f4t: "Fiscal francês",
    f4d: "SIG, Seuil, Bilan, TVA e orçamentos no padrão contábil francês — prontos para o cabinet comptable.",
    f5t: "Intra-grupo (intercompany)",
    f5d: "Faturamento entre as empresas do grupo com espelhamento automático e regras de comissão consistentes.",
    f6t: "Relatórios ao contador",
    f6d: "Extrato conciliado e relatório de faturas em PDF/CSV, com links dos comprovantes — gerados e enviados em 1 clique.",
    f7t: "Multiempresa",
    f7d: "Gerencie várias empresas do grupo com um seletor único, dados isolados e níveis de acesso por usuário.",
    f8t: "Multi-idioma",
    f8d: "Interface em francês, português e inglês; documentos ao contador emitidos em francês.",
    b1t: "Menos planilha, mais controle",
    b1d: "Do orçamento ao recebimento, tudo conectado — sem retrabalho nem dados soltos.",
    b2t: "Pronto para a França",
    b2d: "TVA (incl. autoliquidação), SIG/Bilan e relatórios no formato que o contador espera.",
    b3t: "Seguro e auditável",
    b3d: "Níveis de acesso (super admin · admin · usuário · visualizador) e trilha de auditoria completa.",
    finalTitlePre: "Pronto para gerir tudo",
    finalTitleAccent: "num só painel?",
    finalSubtitle: "Acesse com sua conta do grupo Storm e comece agora.",
    footerRights: "Todos os direitos reservados",
  },
};

const fr: Messages = {
  common: {
    loading: "Chargement…",
    cancel: "Annuler",
    submit: "Envoyer",
    back: "Retour",
    next: "Suivant",
    previous: "Précédent",
    save: "Enregistrer",
    close: "Fermer",
    logout: "Déconnexion",
    logoutPending: "Déconnexion…",
    showPassword: "Afficher le mot de passe",
    hidePassword: "Masquer le mot de passe",
    requiredField: "Champ obligatoire",
  },
  langSwitcher: {
    ariaLabel: "Langue",
  },
  auth: {
    subtitle: "Système de gestion",
    emailLabel: "E-mail",
    emailPlaceholder: "votre@email.com",
    passwordLabel: "Mot de passe",
    passwordPlaceholder: "••••••••",
    newPasswordLabel: "Nouveau mot de passe",
    confirmPasswordLabel: "Confirmer le mot de passe",
    nameLabel: "Nom complet",
    submitLogin: "Connexion",
    submitLoginPending: "Connexion…",
    submitForgot: "Envoyer le lien",
    submitForgotPending: "Envoi en cours…",
    submitReset: "Réinitialiser",
    submitResetPending: "Réinitialisation…",
    submitAccept: "Accepter l’invitation",
    submitAcceptPending: "Acceptation…",
    forgotPasswordLink: "Mot de passe oublié",
    backToLoginLink: "Retour à la connexion",
    forgotTitle: "Mot de passe oublié",
    forgotDescription:
      "Saisissez votre e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.",
    forgotSent: "Envoyé ! Vérifiez votre boîte mail.",
    resetTitle: "Définir un nouveau mot de passe",
    resetDescription: "Choisissez un mot de passe robuste (minimum 8 caractères).",
    resetSuccess: "Mot de passe réinitialisé. Redirection…",
    acceptTitle: "Accepter l’invitation",
    acceptDescription: "Créez votre mot de passe pour finaliser l’inscription.",
    acceptSuccess: "Inscription terminée ! Redirection…",
    errorPasswordsDoNotMatch: "Les mots de passe ne correspondent pas.",
    errorTokenInvalid: "Lien invalide ou expiré.",
  },
  nav: {
    dashboardTitle: "Tableau de bord",
    dashboardSubtitle: "Tableau de bord",
    sectionMasterData: "Données maîtres",
    sectionOperations: "Opérations",
    sectionBilling: "Facturation et reversements",
    sectionFiscalReports: "Fiscal et rapports",
    sectionBank: "Banque",
    sectionPurchases: "Achats",
    sectionFiscal: "Fiscal",
    sectionFinance: "Banque et finances",
    sectionAdmin: "Outils et administration",
    sectionPeople: "Clients et équipe",
    customers: "Clients",
    vehicles: "Registre des véhicules",
    technicians: "Techniciens",
    bankAccounts: "Comptes bancaires",
    dossiers: "Véhicules",
    importEdsp: "Importation eDSP",
    proformas: "Proformas",
    faturas: "Factures",
    controle: "À recevoir et à payer",
    bankExtracts: "Relevés & Réconciliation",
    partnerAccount: "Compte associé",
    suppliers: "Fournisseurs",
    purchaseInvoices: "Factures d’achat",
    fiscalDashboard: "KPIs Fiscaux",
    fiscalResultat: "Compte de résultat",
    fiscalSig: "SIG",
    fiscalSeuil: "Seuil de rentabilité",
    fiscalBilan: "Bilan",
    fiscalBudget: "Budget N+1",
    fiscalEntries: "Écritures",
    fiscalTva: "TVA",
    stormFin: "Storm.Fin (comptabilité) ↗",
    financeImportar: "Importer",
    financeLancamentos: "Écritures",
    financeKpis: "Trésorerie",
    financeFechamento: "Clôture",
    companiesAdmin: "Entreprises",
    users: "Utilisateurs",
    teamNotices: "Messages équipe",
    tasks: "Tâches",
    invites: "Invitations",
    auditLog: "Journal d’audit",
    profileTitle: "Votre profil",
    profileName: "Nom",
    profileEmail: "E-mail",
    profileSuperAdmin: "Super admin",
    profileMemberships: "Adhésions",
    profileMembershipsNone: "aucune",
    companiesAccessible: "Sociétés accessibles",
    companiesNone: "Aucune société disponible.",
    inactive: "inactive",
  },
  shell: {
    searchPlaceholder: "Rechercher (n° facture, dossier, plaque, client)…",
    bankPendingBadgeTitle: "Transactions bancaires en attente de traitement",
    openMenu: "Ouvrir le menu",
    idleTitle: "Session sur le point d'expirer",
    idleBody: "Inactivité détectée. Par sécurité, vous serez déconnecté automatiquement dans :",
    idleStay: "Rester connecté",
    idleLogoutNow: "Se déconnecter",
    searchLoading: "Recherche…",
    searchEmpty: "Aucun dossier trouvé.",
    readOnly: "Lecture seule",
    notifications: "Notifications",
    themeLight: "Thème clair",
    themeDark: "Thème sombre",
    sectionReports: "Rapports",
    reportConsolidated: "Rapport consolidé",
    technicianPayments: "Paiements techniciens",
    companyPrimary: "société principale",
    companyGroup: "groupe",
    roleSuperAdmin: "Super admin",
    roleAdmin: "Admin",
    roleUser: "Utilisateur",
    roleViewer: "Lecteur",
  },
  dash: {
    groupPerformance: "Performance",
    groupTreasury: "Trésorerie",
    treasuryCurrent: "solde actuel",
    periodMonth: "Mois",
    periodQuarter: "Trim.",
    periodYear: "Année",
    periodAll: "Tout",
    periodAllLabel: "toutes périodes",
    kpiCaFacture: "CA facturé (net)",
    kpiBenefice: "Bénéfice prévu (ES)",
    kpiDevise: "Montant devisé",
    kpiDossiers: "Dossiers",
    kpiArClients: "À recevoir · clients",
    kpiOverdue: "En retard · clients",
    kpiApSuppliers: "À payer · fournisseurs",
    kpiApTechnicians: "À payer · techniciens",
    unitDossiers: "dossiers",
    unitInvoices: "factures",
    ofRevenue: "du CA",
    marge: "marge",
    totalQuotes: "total des devis",
    currentPeriod: "période en cours",
    overdueDetail: "échéance dépassée",
    intraGroupReceivable: "Intra-groupe à recevoir",
  },
  landing: {
    navCta: "Accéder au système",
    techSignupLink: "Je suis technicien — créer mon compte",
    heroEyebrow: "ERP financier · débosselage",
    heroTitlePre: "Du dossier au comptable,",
    heroTitleAccent: "en un seul endroit.",
    heroSubtitle:
      "Commissions, facturation, rapprochement bancaire et fiscalité française — multi-société, en temps réel. Le système de gestion conçu pour l'activité de réparation grêle du groupe Storm.",
    featuresTitle: "Tout ce dont l'activité a besoin",
    featuresSubtitle:
      "De l'entrée du véhicule à la clôture comptable — un flux unique, sans tableurs parallèles.",
    f1t: "Dossiers & commissions",
    f1d: "Chaque réparation au même endroit : lignes de service, cascade de commission (client · technicien · intermédiaire) et bénéfice prévu, en temps réel.",
    f2t: "Facturation complète",
    f2d: "Factures client, technicien et intra-groupe (proformas) — PDF automatique, envoi par e-mail et avoirs.",
    f3t: "Rapprochement bancaire",
    f3d: "Importez le relevé et rapprochez en quelques clics : encaissements, paiements, impôts et frais — avec justificatifs joints.",
    f4t: "Fiscalité française",
    f4d: "SIG, Seuil, Bilan, TVA et budgets au format comptable français — prêts pour le cabinet comptable.",
    f5t: "Intra-groupe (intercompany)",
    f5d: "Facturation entre les sociétés du groupe avec miroir automatique et règles de commission cohérentes.",
    f6t: "Rapports au comptable",
    f6d: "Relevé rapproché et rapport de factures en PDF/CSV, avec liens des justificatifs — générés et envoyés en 1 clic.",
    f7t: "Multi-société",
    f7d: "Gérez plusieurs sociétés du groupe avec un sélecteur unique, des données isolées et des niveaux d'accès par utilisateur.",
    f8t: "Multilingue",
    f8d: "Interface en français, portugais et anglais ; documents au comptable émis en français.",
    b1t: "Moins de tableurs, plus de contrôle",
    b1d: "Du devis à l'encaissement, tout est connecté — sans ressaisie ni données éparses.",
    b2t: "Prêt pour la France",
    b2d: "TVA (incl. autoliquidation), SIG/Bilan et rapports au format attendu par le comptable.",
    b3t: "Sûr et auditable",
    b3d: "Niveaux d'accès (super admin · admin · utilisateur · lecteur) et piste d'audit complète.",
    finalTitlePre: "Prêt à tout gérer",
    finalTitleAccent: "dans un seul tableau de bord ?",
    finalSubtitle: "Connectez-vous avec votre compte du groupe Storm et commencez.",
    footerRights: "Tous droits réservés",
  },
};

const en: Messages = {
  common: {
    loading: "Loading…",
    cancel: "Cancel",
    submit: "Submit",
    back: "Back",
    next: "Next",
    previous: "Previous",
    save: "Save",
    close: "Close",
    logout: "Sign out",
    logoutPending: "Signing out…",
    showPassword: "Show password",
    hidePassword: "Hide password",
    requiredField: "Required field",
  },
  langSwitcher: {
    ariaLabel: "Language",
  },
  auth: {
    subtitle: "Management system",
    emailLabel: "Email",
    emailPlaceholder: "your@email.com",
    passwordLabel: "Password",
    passwordPlaceholder: "••••••••",
    newPasswordLabel: "New password",
    confirmPasswordLabel: "Confirm password",
    nameLabel: "Full name",
    submitLogin: "Sign in",
    submitLoginPending: "Signing in…",
    submitForgot: "Send link",
    submitForgotPending: "Sending…",
    submitReset: "Reset password",
    submitResetPending: "Resetting…",
    submitAccept: "Accept invite",
    submitAcceptPending: "Accepting…",
    forgotPasswordLink: "Forgot my password",
    backToLoginLink: "Back to sign in",
    forgotTitle: "Recover password",
    forgotDescription: "Enter your email and we will send a link to reset your password.",
    forgotSent: "Sent! Check your email.",
    resetTitle: "Set a new password",
    resetDescription: "Choose a strong password (minimum 8 characters).",
    resetSuccess: "Password reset successful. Redirecting…",
    acceptTitle: "Accept invite",
    acceptDescription: "Create your password to finish signing up.",
    acceptSuccess: "Signup complete! Redirecting…",
    errorPasswordsDoNotMatch: "Passwords do not match.",
    errorTokenInvalid: "Invalid or expired link.",
  },
  nav: {
    dashboardTitle: "Dashboard",
    dashboardSubtitle: "Control panel",
    sectionMasterData: "Master data",
    sectionOperations: "Operations",
    sectionBilling: "Billing and payouts",
    sectionFiscalReports: "Tax and reports",
    sectionBank: "Bank",
    sectionPurchases: "Purchases",
    sectionFiscal: "Fiscal",
    sectionFinance: "Bank and finance",
    sectionAdmin: "Tools and administration",
    sectionPeople: "Customers and team",
    customers: "Customers",
    vehicles: "Vehicle register",
    technicians: "Technicians",
    bankAccounts: "Bank accounts",
    dossiers: "Cars",
    importEdsp: "eDSP Import",
    proformas: "Proformas",
    faturas: "Invoices",
    controle: "Receivables and payables",
    bankExtracts: "Statements & Reconciliation",
    partnerAccount: "Partner account",
    suppliers: "Suppliers",
    purchaseInvoices: "Purchase invoices",
    fiscalDashboard: "Fiscal KPIs",
    fiscalResultat: "P&L Statement",
    fiscalSig: "SIG",
    fiscalSeuil: "Break-even point",
    fiscalBilan: "Balance Sheet",
    fiscalBudget: "Budget N+1",
    fiscalEntries: "Entries",
    fiscalTva: "VAT",
    stormFin: "Storm.Fin (accounting) ↗",
    financeImportar: "Import",
    financeLancamentos: "Entries",
    financeKpis: "Treasury",
    financeFechamento: "Closing",
    companiesAdmin: "Companies",
    users: "Users",
    teamNotices: "Team notices",
    tasks: "Tasks",
    invites: "Invites",
    auditLog: "Audit log",
    profileTitle: "Your profile",
    profileName: "Name",
    profileEmail: "Email",
    profileSuperAdmin: "Super admin",
    profileMemberships: "Memberships",
    profileMembershipsNone: "none",
    companiesAccessible: "Accessible companies",
    companiesNone: "No companies available.",
    inactive: "inactive",
  },
  shell: {
    searchPlaceholder: "Search (invoice no., case, plate, client)…",
    bankPendingBadgeTitle: "Bank transactions awaiting handling",
    openMenu: "Open menu",
    idleTitle: "Session about to expire",
    idleBody: "Inactivity detected. For your security, you'll be signed out automatically in:",
    idleStay: "Stay signed in",
    idleLogoutNow: "Sign out now",
    searchLoading: "Searching…",
    searchEmpty: "No case found.",
    readOnly: "Read only",
    notifications: "Notifications",
    themeLight: "Light theme",
    themeDark: "Dark theme",
    sectionReports: "Reports",
    reportConsolidated: "Consolidated report",
    technicianPayments: "Technician payments",
    companyPrimary: "main company",
    companyGroup: "group",
    roleSuperAdmin: "Super admin",
    roleAdmin: "Admin",
    roleUser: "User",
    roleViewer: "Viewer",
  },
  dash: {
    groupPerformance: "Performance",
    groupTreasury: "Treasury",
    treasuryCurrent: "current balance",
    periodMonth: "Month",
    periodQuarter: "Qtr",
    periodYear: "Year",
    periodAll: "All",
    periodAllLabel: "all periods",
    kpiCaFacture: "Revenue (net)",
    kpiBenefice: "Forecast profit (ES)",
    kpiDevise: "Quoted amount",
    kpiDossiers: "Cases",
    kpiArClients: "Receivable · clients",
    kpiOverdue: "Overdue · clients",
    kpiApSuppliers: "Payable · suppliers",
    kpiApTechnicians: "Payable · technicians",
    unitDossiers: "cases",
    unitInvoices: "invoices",
    ofRevenue: "of revenue",
    marge: "margin",
    totalQuotes: "total quotes",
    currentPeriod: "current period",
    overdueDetail: "past due",
    intraGroupReceivable: "Intra-group receivable",
  },
  landing: {
    navCta: "Open the system",
    techSignupLink: "I am a technician — create my account",
    heroEyebrow: "Financial ERP · débosselage",
    heroTitlePre: "From case to accountant,",
    heroTitleAccent: "all in one place.",
    heroSubtitle:
      "Commissions, invoicing, bank reconciliation and French tax — multi-company, in real time. The management system built for the Storm group's hail-repair operation.",
    featuresTitle: "Everything the operation needs",
    featuresSubtitle:
      "From vehicle intake to accounting close — a single flow, no side spreadsheets.",
    f1t: "Cases & commissions",
    f1d: "Every repair in one place: service lines, commission cascade (client · technician · intermediary) and projected profit, in real time.",
    f2t: "Full invoicing",
    f2d: "Client, technician and intra-group (proforma) invoices — automatic PDF, email sending and credit notes.",
    f3t: "Bank reconciliation",
    f3d: "Import the statement and reconcile in a few clicks: receipts, payments, taxes and fees — with attached vouchers.",
    f4t: "French accounting",
    f4d: "SIG, Seuil, Bilan, VAT and budgets in the French accounting format — ready for the cabinet comptable.",
    f5t: "Intra-group (intercompany)",
    f5d: "Invoicing between group companies with automatic mirroring and consistent commission rules.",
    f6t: "Reports to the accountant",
    f6d: "Reconciled statement and invoice report in PDF/CSV, with voucher links — generated and sent in 1 click.",
    f7t: "Multi-company",
    f7d: "Manage several group companies with a single switcher, isolated data and per-user access levels.",
    f8t: "Multilingual",
    f8d: "Interface in French, Portuguese and English; accountant documents issued in French.",
    b1t: "Less spreadsheet, more control",
    b1d: "From quote to receipt, everything connected — no rework or scattered data.",
    b2t: "Ready for France",
    b2d: "VAT (incl. reverse charge), SIG/Bilan and reports in the format the accountant expects.",
    b3t: "Secure and auditable",
    b3d: "Access levels (super admin · admin · user · viewer) and a full audit trail.",
    finalTitlePre: "Ready to manage it all",
    finalTitleAccent: "in a single dashboard?",
    finalSubtitle: "Sign in with your Storm group account and get started.",
    footerRights: "All rights reserved",
  },
};

/* Composição base + páginas (arquivo gerado). As chaves de página ficam no
   nível superior de cada locale, então o key-path continua `namespace.key`. */
export type AllMessages = Messages & PagesMessages;

export const MESSAGES: Record<Locale, AllMessages> = {
  pt: { ...pt, ...ptPages },
  fr: { ...fr, ...frPages },
  en: { ...en, ...enPages },
  // F-locale-es — espanhol: mesma estratégia do ucraniano (abaixo).
  es: {
    ...fr,
    ...frPages,
    common: esCommon,
    langSwitcher: esLangSwitcher,
    auth: esAuth,
    techPortal: esTechPortal,
    // F-tech-signup — a porta PÚBLICA (home + formulário) precisa estar no
    // idioma de quem chega; o resto do ERP segue em francês.
    landing: esLanding,
    techSignup: esTechSignup,
  },
  // F-locale-uk — ucraniano: traduzido o que o TÉCNICO vê (auth/common/portal);
  // o resto do ERP cai no FRANCÊS até ser traduzido (messages.uk.ts).
  uk: {
    ...fr,
    ...frPages,
    common: ukCommon,
    langSwitcher: ukLangSwitcher,
    auth: ukAuth,
    techPortal: ukTechPortal,
    landing: ukLanding,
    techSignup: ukTechSignup,
  },
};
